import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { AlertTriangle, Edit, Plus, Printer, Save, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ICS232Form = ({ currentExercise, theme }) => {
  const API = API_BASE_URL || '';
  const [view, setView] = useState('list');
  const [forms, setForms] = useState([]);
  const [editingForm, setEditingForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    exercise_id: currentExercise?.id || '',
    exercise_name: currentExercise?.name || '',
    operational_period_from: '',
    operational_period_to: '',
    environmental_sites: [{ id: 1, site_number: '', priority: '', site_name_location: '', site_issues: '' }],
    environmental_narrative: '',
    archaeo_sites: [{ id: 1, site_number: '', priority: '', site_name_location: '', site_issues: '' }],
    archaeo_narrative: '',
    prepared_by: '',
    prepared_date_time: '',
    signature: ''
  });

  // Priority options with color backgrounds
  const priorityOptions = [
    { value: 'Pri 1', label: 'Pri 1', bgColor: 'bg-red-600', textColor: 'text-white' },
    { value: 'Pri 2', label: 'Pri 2', bgColor: 'bg-orange-600', textColor: 'text-white' },
    { value: 'Pri 3', label: 'Pri 3', bgColor: 'bg-amber-600', textColor: 'text-white' },
    { value: 'Pri 4', label: 'Pri 4', bgColor: 'bg-yellow-500', textColor: 'text-black' },
    { value: 'Pri 5', label: 'Pri 5', bgColor: 'bg-green-600', textColor: 'text-white' }
  ];

  const getPriorityColor = (priority) => {
    const option = priorityOptions.find(opt => opt.value === priority);
    return option ? option.bgColor : 'bg-gray-700';
  };

  // Set exercise name when currentExercise changes
  useEffect(() => {
    if (currentExercise && currentExercise.name) {
      setFormData(prev => ({
        ...prev,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.name
      }));
    }
  }, [currentExercise]);

  // Fetch forms from backend
  useEffect(() => {
    if (currentExercise?.id) {
      fetchForms();
    }
  }, [currentExercise]);

  const fetchForms = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/api/resources-at-risk?exercise_id=${currentExercise.id}`, { headers });
      setForms(response.data);
      console.log('Fetched ICS 232 forms:', response.data);
    } catch (error) {
      console.error('Error fetching resources at risk forms:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSiteChange = (type, siteIndex, field, value) => {
    const siteType = type === 'environmental' ? 'environmental_sites' : 'archaeo_sites';
    const updatedSites = [...formData[siteType]];
    updatedSites[siteIndex] = { ...updatedSites[siteIndex], [field]: value };
    setFormData(prev => ({ ...prev, [siteType]: updatedSites }));
  };

  const addSite = (type) => {
    const siteType = type === 'environmental' ? 'environmental_sites' : 'archaeo_sites';
    const currentSites = formData[siteType];
    const newId = currentSites.length > 0 ? Math.max(...currentSites.map(s => s.id)) + 1 : 1;
    setFormData(prev => ({
      ...prev,
      [siteType]: [...currentSites, { id: newId, site_number: '', priority: '', site_name_location: '', site_issues: '' }]
    }));
  };

  const removeSite = (type, siteId) => {
    const siteType = type === 'environmental' ? 'environmental_sites' : 'archaeo_sites';
    const updatedSites = formData[siteType].filter(site => site.id !== siteId);
    if (updatedSites.length === 0) {
      setFormData(prev => ({ ...prev, [siteType]: [{ id: 1, site_number: '', priority: '', site_name_location: '', site_issues: '' }] }));
    } else {
      setFormData(prev => ({ ...prev, [siteType]: updatedSites }));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (editingForm) {
        await axios.put(`${API}/api/resources-at-risk/${editingForm.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/api/resources-at-risk`, formData, { headers });
      }
      await fetchForms();
      setView('list');
      setEditingForm(null);
      resetForm();
    } catch (error) {
      console.error('Error saving form:', error);
      alert(`Failed to save form: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Save individual card data (saves the entire form)
  const saveCard = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (editingForm) {
        await axios.put(`${API}/api/resources-at-risk/${editingForm.id}`, formData, { headers });
        // Update the editingForm reference with new data
        setEditingForm({ ...editingForm, ...formData });
      } else {
        // Create new form first
        const response = await axios.post(`${API}/api/resources-at-risk`, formData, { headers });
        setEditingForm(response.data);
      }
      await fetchForms();
      alert('Changes saved successfully!');
    } catch (error) {
      console.error('Error saving card:', error);
      alert(`Failed to save: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (form) => {
    setEditingForm(form);
    setFormData({
      exercise_id: form.exercise_id,
      exercise_name: form.exercise_name,
      operational_period_from: form.operational_period_from,
      operational_period_to: form.operational_period_to,
      environmental_sites: form.environmental_sites.length > 0 ? form.environmental_sites : [{ id: 1, site_number: '', priority: '', site_name_location: '', site_issues: '' }],
      environmental_narrative: form.environmental_narrative,
      archaeo_sites: form.archaeo_sites.length > 0 ? form.archaeo_sites : [{ id: 1, site_number: '', priority: '', site_name_location: '', site_issues: '' }],
      archaeo_narrative: form.archaeo_narrative,
      prepared_by: form.prepared_by,
      prepared_date_time: form.prepared_date_time,
      signature: form.signature || ''
    });
    setView('add');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/api/resources-at-risk/${id}`, { headers });
        await fetchForms();
      } catch (error) {
        console.error('Error deleting form:', error);
        alert(`Failed to delete form: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      exercise_id: currentExercise?.id || '',
      exercise_name: currentExercise?.name || '',
      operational_period_from: '',
      operational_period_to: '',
      environmental_sites: [{ id: 1, site_number: '', priority: '', site_name_location: '', site_issues: '' }],
      environmental_narrative: '',
      archaeo_sites: [{ id: 1, site_number: '', priority: '', site_name_location: '', site_issues: '' }],
      archaeo_narrative: '',
      prepared_by: '',
      prepared_date_time: '',
      signature: ''
    });
  };

  const printICS232 = (formOverride = null) => {
    const formToPrint = formOverride || editingForm || formData;
    
    if (!formToPrint) {
      alert('No form data to print.');
      return;
    }

    console.log(`Printing ICS 232 with ${formToPrint.environmental_sites?.length || 0} environmental sites and ${formToPrint.archaeo_sites?.length || 0} archaeo sites`);
    
    const printContent = `
      <html>
        <head>
          <title>ICS 232 - Resources at Risk Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            h1 { font-size: 20px; margin: 0; }
            .form-section { margin-bottom: 15px; }
            .form-row { display: flex; gap: 20px; margin-bottom: 8px; }
            .form-field { flex: 1; }
            .label { font-weight: bold; margin-bottom: 3px; font-size: 11px; }
            .value { border-bottom: 1px solid #000; min-height: 18px; padding: 2px; }
            .section-title { color: #ea580c; font-weight: bold; font-size: 14px; margin: 15px 0 10px 0; }
            .site-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .site-table th, .site-table td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 10px; }
            .site-table th { background-color: #f5f5f5; font-weight: bold; }
            .narrative-box { border: 1px solid #000; padding: 10px; min-height: 80px; margin: 10px 0; white-space: pre-wrap; }
            .signature-line { border-bottom: 1px solid #000; min-height: 40px; margin-top: 5px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 232 - RESOURCES AT RISK SUMMARY</h1>
            <h2 style="margin-top: 10px; font-size: 16px; color: #ea580c;">${formToPrint.exercise_name || 'Exercise Name Not Set'}</h2>
          </div>
          
          <div class="form-section">
            <div class="form-row">
              <div class="form-field">
                <div class="label">Operational Period - From:</div>
                <div class="value">${formToPrint.operational_period_from || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">To:</div>
                <div class="value">${formToPrint.operational_period_to || ''}</div>
              </div>
            </div>
          </div>

          <div class="section-title">Environmental-Sensitive Areas and Wildlife Issues</div>
          <table class="site-table">
            <thead>
              <tr>
                <th style="width: 10%;">Site #</th>
                <th style="width: 15%;">Priority</th>
                <th style="width: 35%;">Site Name and/or Physical Location</th>
                <th style="width: 40%;">Site Issues</th>
              </tr>
            </thead>
            <tbody>
              ${(formToPrint.environmental_sites || []).map(site => `
                <tr>
                  <td>${site.site_number || ''}</td>
                  <td>${site.priority || ''}</td>
                  <td>${site.site_name_location || ''}</td>
                  <td>${site.site_issues || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div>
            <div class="label">Narrative:</div>
            <div class="narrative-box">${formToPrint.environmental_narrative || ''}</div>
          </div>

          <div class="section-title">Archaeo-cultural and Socio-economic Issues</div>
          <table class="site-table">
            <thead>
              <tr>
                <th style="width: 10%;">Site #</th>
                <th style="width: 15%;">Priority</th>
                <th style="width: 35%;">Site Name and/or Physical Location</th>
                <th style="width: 40%;">Site Issues</th>
              </tr>
            </thead>
            <tbody>
              ${(formToPrint.archaeo_sites || []).map(site => `
                <tr>
                  <td>${site.site_number || ''}</td>
                  <td>${site.priority || ''}</td>
                  <td>${site.site_name_location || ''}</td>
                  <td>${site.site_issues || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div>
            <div class="label">Narrative:</div>
            <div class="narrative-box">${formToPrint.archaeo_narrative || ''}</div>
          </div>
          
          <div style="margin-top: 30px;">
            <div class="form-row">
              <div class="form-field">
                <div class="label">Prepared by:</div>
                <div class="value">${formToPrint.prepared_by || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">Date/Time:</div>
                <div class="value">${formToPrint.prepared_date_time || ''}</div>
              </div>
            </div>
            <div style="margin-top: 20px;">
              <div class="label">Signature:</div>
              <div class="signature-line">${formToPrint.signature || ''}</div>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.print();
    printWindow.close();
  };

  const printAllICS232 = () => {
    if (forms.length === 0) {
      alert('No ICS 232 forms to print. Please create forms first.');
      return;
    }

    console.log(`Printing ${forms.length} ICS 232 form(s)`);
    
    // Generate content for all forms
    const allFormsContent = forms.map((form, index) => `
      ${index > 0 ? '<div style="page-break-before: always;"></div>' : ''}
      <div class="form-section-wrapper">
        <div class="header">
          <h1>ICS 232 - RESOURCES AT RISK SUMMARY</h1>
          <h2 style="margin-top: 10px; font-size: 16px; color: #ea580c;">${form.exercise_name || 'Exercise Name Not Set'}</h2>
          ${forms.length > 1 ? `<p style="font-size: 12px; color: #666;">Form ${index + 1} of ${forms.length}</p>` : ''}
        </div>
        
        <div class="form-section">
          <div class="form-row">
            <div class="form-field">
              <div class="label">Operational Period - From:</div>
              <div class="value">${form.operational_period_from || ''}</div>
            </div>
            <div class="form-field">
              <div class="label">To:</div>
              <div class="value">${form.operational_period_to || ''}</div>
            </div>
          </div>
        </div>

        <div class="section-title">Environmental-Sensitive Areas and Wildlife Issues</div>
        <table class="site-table">
          <thead>
            <tr>
              <th style="width: 10%;">Site #</th>
              <th style="width: 15%;">Priority</th>
              <th style="width: 35%;">Site Name and/or Physical Location</th>
              <th style="width: 40%;">Site Issues</th>
            </tr>
          </thead>
          <tbody>
            ${(form.environmental_sites || []).map(site => `
              <tr>
                <td>${site.site_number || ''}</td>
                <td>${site.priority || ''}</td>
                <td>${site.site_name_location || ''}</td>
                <td>${site.site_issues || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div>
          <div class="label">Narrative:</div>
          <div class="narrative-box">${form.environmental_narrative || ''}</div>
        </div>

        <div class="section-title">Archaeo-cultural and Socio-economic Issues</div>
        <table class="site-table">
          <thead>
            <tr>
              <th style="width: 10%;">Site #</th>
              <th style="width: 15%;">Priority</th>
              <th style="width: 35%;">Site Name and/or Physical Location</th>
              <th style="width: 40%;">Site Issues</th>
            </tr>
          </thead>
          <tbody>
            ${(form.archaeo_sites || []).map(site => `
              <tr>
                <td>${site.site_number || ''}</td>
                <td>${site.priority || ''}</td>
                <td>${site.site_name_location || ''}</td>
                <td>${site.site_issues || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div>
          <div class="label">Narrative:</div>
          <div class="narrative-box">${form.archaeo_narrative || ''}</div>
        </div>

        <div style="margin-top: 30px;">
          <div class="form-row">
            <div class="form-field">
              <div class="label">Prepared by:</div>
              <div class="value">${form.prepared_by || ''}</div>
            </div>
            <div class="form-field">
              <div class="label">Date/Time:</div>
              <div class="value">${form.prepared_date_time || ''}</div>
            </div>
          </div>
          <div class="form-field" style="margin-top: 10px;">
            <div class="label">Signature:</div>
            <div class="signature-line">${form.signature || ''}</div>
          </div>
        </div>
      </div>
    `).join('');
    
    const printContent = `
      <html>
        <head>
          <title>ICS 232 - All Resources at Risk Summaries</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            h1 { font-size: 20px; margin: 0; }
            .form-section-wrapper { margin-bottom: 40px; }
            .form-section { margin-bottom: 15px; }
            .form-row { display: flex; gap: 20px; margin-bottom: 8px; }
            .form-field { flex: 1; }
            .label { font-weight: bold; margin-bottom: 3px; font-size: 11px; }
            .value { border-bottom: 1px solid #000; min-height: 18px; padding: 2px; }
            .section-title { color: #ea580c; font-weight: bold; font-size: 14px; margin: 15px 0 10px 0; }
            .site-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .site-table th, .site-table td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 10px; }
            .site-table th { background-color: #f5f5f5; font-weight: bold; }
            .narrative-box { border: 1px solid #000; padding: 10px; min-height: 80px; margin: 10px 0; white-space: pre-wrap; }
            .signature-line { border-bottom: 1px solid #000; min-height: 40px; margin-top: 5px; }
            @media print {
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          ${allFormsContent}
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.print();
    printWindow.close();
  };

  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-2`}>ICS 232 - Resources at Risk Summary</h2>
            <p className={`${theme.colors.textMuted}`}>
              Identify and track resources at risk during incident operations.
            </p>
          </div>
          <div className="flex gap-2">
            {forms.length > 0 && (
              <Button
                onClick={printAllICS232}
                variant="outline"
                className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All ICS 232
              </Button>
            )}
            <Button
              onClick={() => setView('add')}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              New ICS 232
            </Button>
          </div>
        </div>

        {forms.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className={`h-12 w-12 ${theme.colors.textMuted} mx-auto mb-4`} />
            <h3 className={`text-lg font-semibold ${theme.colors.textPrimary} mb-2`}>No ICS 232 forms yet</h3>
            <p className={`${theme.colors.textMuted} mb-4`}>
              Create your first Resources at Risk Summary form.
            </p>
            <Button
              onClick={() => setView('add')}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First ICS 232
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {forms.map((form) => (
              <Card key={form.id} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-white font-semibold">{form.exercise_name}</h3>
                      <p className="text-gray-400 text-sm">
                        Period: {form.operational_period_from} to {form.operational_period_to}
                      </p>
                      <p className="text-gray-400 text-sm">
                        Environmental Sites: {form.environmental_sites?.length || 0} | Archaeo Sites: {form.archaeo_sites?.length || 0}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printICS232(form)}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        <Printer className="h-3 w-3 mr-1" />
                        Print
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(form)}
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(form.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>
          {editingForm ? 'Edit' : 'Create'} ICS 232 - Resources at Risk Summary
        </h2>
        <div className="flex gap-2">
          <Button
            onClick={() => printICS232()}
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print ICS 232
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Header Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Header Information</CardTitle>
            <Button
              onClick={saveCard}
              disabled={loading}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Header
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">{formData.exercise_name}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Operational Period - From:</Label>
                  <Input
                    type="date"
                    value={formData.operational_period_from}
                    onChange={(e) => handleInputChange('operational_period_from', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">To:</Label>
                  <Input
                    type="date"
                    value={formData.operational_period_to}
                    onChange={(e) => handleInputChange('operational_period_to', e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Environmental Sites Card */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Environmental-Sensitive Areas and Wildlife Issues</CardTitle>
            <Button
              onClick={saveCard}
              disabled={loading}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Environmental Sites
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '10%'}}>Site #</th>
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '15%'}}>Priority</th>
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '30%'}}>Site Name and/or Physical Location</th>
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '35%'}}>Site Issues</th>
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '10%'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.environmental_sites.map((site, index) => (
                    <tr key={site.id} className="border-b border-gray-700">
                      <td className="p-2">
                        <Input
                          value={site.site_number}
                          onChange={(e) => handleSiteChange('environmental', index, 'site_number', e.target.value)}
                          placeholder="Site #"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={site.priority}
                          onValueChange={(value) => handleSiteChange('environmental', index, 'priority', value)}
                        >
                          <SelectTrigger className={`${getPriorityColor(site.priority)} border-gray-700 text-white text-sm`}>
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            {priorityOptions.map(option => (
                              <SelectItem 
                                key={option.value} 
                                value={option.value}
                                className={`${option.bgColor} ${option.textColor} hover:opacity-80`}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          value={site.site_name_location}
                          onChange={(e) => handleSiteChange('environmental', index, 'site_name_location', e.target.value)}
                          placeholder="Site name/location"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={site.site_issues}
                          onChange={(e) => handleSiteChange('environmental', index, 'site_issues', e.target.value)}
                          placeholder="Issues"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeSite('environmental', site.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          title="Delete site"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              onClick={() => addSite('environmental')}
              variant="outline"
              className="mt-4 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Site
            </Button>
            <div className="mt-4">
              <Label className="text-white">Narrative</Label>
              <Textarea
                value={formData.environmental_narrative}
                onChange={(e) => handleInputChange('environmental_narrative', e.target.value)}
                placeholder="Enter narrative..."
                rows={4}
                className="bg-gray-900 border-gray-700 text-white placeholder-gray-400"
              />
            </div>
          </CardContent>
        </Card>

        {/* Archaeo-cultural Sites Card */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Archaeo-cultural and Socio-economic Issues</CardTitle>
            <Button
              onClick={saveCard}
              disabled={loading}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Archaeo Sites
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '10%'}}>Site #</th>
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '15%'}}>Priority</th>
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '30%'}}>Site Name and/or Physical Location</th>
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '35%'}}>Site Issues</th>
                    <th className="text-left text-white text-sm font-semibold p-2" style={{width: '10%'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.archaeo_sites.map((site, index) => (
                    <tr key={site.id} className="border-b border-gray-700">
                      <td className="p-2">
                        <Input
                          value={site.site_number}
                          onChange={(e) => handleSiteChange('archaeo', index, 'site_number', e.target.value)}
                          placeholder="Site #"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={site.priority}
                          onValueChange={(value) => handleSiteChange('archaeo', index, 'priority', value)}
                        >
                          <SelectTrigger className={`${getPriorityColor(site.priority)} border-gray-700 text-white text-sm`}>
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            {priorityOptions.map(option => (
                              <SelectItem 
                                key={option.value} 
                                value={option.value}
                                className={`${option.bgColor} ${option.textColor} hover:opacity-80`}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          value={site.site_name_location}
                          onChange={(e) => handleSiteChange('archaeo', index, 'site_name_location', e.target.value)}
                          placeholder="Site name/location"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={site.site_issues}
                          onChange={(e) => handleSiteChange('archaeo', index, 'site_issues', e.target.value)}
                          placeholder="Issues"
                          className="bg-gray-900 border-gray-700 text-white text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeSite('archaeo', site.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          title="Delete site"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              onClick={() => addSite('archaeo')}
              variant="outline"
              className="mt-4 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Site
            </Button>
            <div className="mt-4">
              <Label className="text-white">Narrative</Label>
              <Textarea
                value={formData.archaeo_narrative}
                onChange={(e) => handleInputChange('archaeo_narrative', e.target.value)}
                placeholder="Enter narrative..."
                rows={4}
                className="bg-gray-900 border-gray-700 text-white placeholder-gray-400"
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-orange-500">Preparer Information</CardTitle>
            <Button
              onClick={saveCard}
              disabled={loading}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Preparer Info
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Prepared by</Label>
                <Input
                  value={formData.prepared_by}
                  onChange={(e) => handleInputChange('prepared_by', e.target.value)}
                  placeholder="Enter preparer name"
                  className="bg-gray-900 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-white">Date/Time</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.prepared_date_time ? formData.prepared_date_time.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.prepared_date_time ? formData.prepared_date_time.split('T')[1] : '00:00';
                      handleInputChange('prepared_date_time', `${date}T${time}`);
                    }}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.prepared_date_time ? formData.prepared_date_time.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.prepared_date_time ? formData.prepared_date_time.split('T')[0] : '';
                        handleInputChange('prepared_date_time', `${date}T${value}`);
                      }
                    }}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-white">Signature</Label>
              <Input
                value={formData.signature}
                onChange={(e) => handleInputChange('signature', e.target.value)}
                placeholder="Enter signature"
                className="bg-gray-900 border-gray-700 text-white placeholder-gray-400"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pb-8">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingForm(null);
              resetForm();
            }}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingForm ? 'Update' : 'Save')} ICS 232
          </Button>
        </div>
      </div>
    </div>
  );
};

// ICS 233 - Incident Open Action Tracker Form

export default ICS232Form;
